/* eslint-disable import/no-unresolved */
/* eslint-disable jsx-a11y/control-has-associated-label */
import { useEffect, useState } from 'react';
import { useQuery, useMutation, useReactiveVar } from '@apollo/client';
import { List, ListItemText, Tooltip } from 'appkit-react';
import { useHistory } from 'react-router-dom';

import { GET_TENANTS } from 'Apollo/Queries';
import { DELETE_TENANT, IAM_CHANGE_ORGNIZATION_STATUS } from 'Apollo/Mutations';
import {
    tenantIdVar, isAuthenticatedVar, getUserInfoVar, leftNavigationItemsVar,
    showCrossReportConfigVar, showDataReconMenusVar, showReportMenuVar,
    currentLeftMenuVar, moduleIdVar,
} from 'Apollo/config';
import { ModuleEnum } from 'constants';
import { signOutUser } from 'views/Auth/AuthHelper';
import { listItems } from './utils';
import Tenants from '../components/Tenants';

const productName = process.env.REACT_APP_PRODUCT;

const TenantContainer = () => {
    const history = useHistory();
    const tenantVar = useReactiveVar(tenantIdVar);
    const [showWarningModal, setShowWarningModal] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const [selectedTenant, setSelectedTenant] = useState(null);
    const [active, setActive] = useState(false);
    const [inactive, setInactive] = useState(false);
    const [filterString, setFilterString] = useState('');
    const [successMsg, setSuccessMsg] = useState('');
    const [showSuccessAlert, setShowSuccessAlert] = useState('');
    const [errorMsg, setErrorMsg] = useState('');
    const [showErrorAlert, setShowErrorAlert] = useState(false);
    const [tableData, setTableData] = useState([]);

    /* Apollo query to fetch tenant details */
    const {
        client, loading: tenantLoading, data = {}, refetch,
    } = useQuery(GET_TENANTS, {
        fetchPolicy: 'no-cache',
    });

    useEffect(() => {
        leftNavigationItemsVar(listItems);
        showCrossReportConfigVar(false);
        showDataReconMenusVar(false);
        showReportMenuVar(false);
        currentLeftMenuVar(ModuleEnum.TENANT);
    }, []);

    /* Side effect when tenant details updates */
    useEffect(() => {
        if (data && data.getTenants) {
            setTableData(data.getTenants);
        }
    }, [data]);

    /* Side effect when filterString updates */
    useEffect(() => {
        const searchString = filterString.trim().toLowerCase();
        if (searchString.length > 0) {
            setTableData(data && data.getTenants.filter(e => e.name.toLowerCase().match(searchString)));
        } else {
            setTableData(data && data.getTenants);
        }
    }, [filterString]);

    /* Side effect when active or inactive status updates */
    useEffect(() => {
        // eslint-disable-next-line no-lonely-if
        if (active === false && inactive === false) {
            setTableData(data && data.getTenants);
        } else if (active === true && inactive === false) {
            setTableData(data && data.getTenants && data && data.getTenants.filter(e => e.iamStatus === 'ACTIVE'));
        } else if (active === false && inactive === true) {
            setTableData(data && data.getTenants && data && data.getTenants.filter(e => e.iamStatus === 'INACTIVE'));
        } else {
            setTableData(data && data.getTenants);
        }
    }, [active, inactive]);

    /* Apollo mutation to change orgnization status in IAM */
    const [iamChangeTenantStatus, { loading: isChangeTenantStatusLoading }] = useMutation(IAM_CHANGE_ORGNIZATION_STATUS);

    /* Breadcrumb items configuration */
    const breadcrumbItems = [
        {
            link: '',
            value: (
                <span
                    className="appkiticon icon-home-fill font-16"
                    onClick={
                        () => {
                            showReportMenuVar(false);
                            history.push('/');
                            showCrossReportConfigVar(false);
                            leftNavigationItemsVar([]);
                            moduleIdVar(null);
                        }
                    }
                    role="presentation"
                />
            ),
        },
        {
            link: '',
            value: <span className="a-breadcrumb-item-text">Tenant Management</span>,
        },
    ];

    /* Apollo mutation to delete tenant */
    const [deleteTenant, { loading: deleteTenantLoading }] = useMutation(DELETE_TENANT);

    /** Calls iamChangeTenantStatus and deleteTenant */
    const callDeleteTenant = async () => {
        try {
            const _iamStatusRes = await iamChangeTenantStatus({
                variables: {
                    input: {
                        organizationId: selectedTenant.iamOrganizationId,
                        action: 'DISABLE',
                        product: productName,
                    },
                },
            });
            if (_iamStatusRes.data.IAMchangeOrganizationStatus.status === 'SUCCESS') {
                try {
                    const deleteTenantRes = await deleteTenant({
                        variables: {
                            input: {
                                tenantId: selectedTenant.tenantId,
                            },
                        },
                    });
                    if (!deleteTenantLoading && deleteTenantRes.data.deleteTenant && tenantVar === selectedTenant.tenantId) {
                        signOutUser();
                        client.clearStore();
                        isAuthenticatedVar(false);
                        getUserInfoVar({});
                        history.push('/login');
                    } else {
                        setSuccessMsg('Tenant Deleted Successfully!');
                        setShowSuccessAlert(true);
                        refetch();
                    }
                } catch (deleteTenantErr) {
                    setErrorMsg('An error occurred while deleting the tenant.');
                    setShowErrorAlert(true);
                }
            }
        } catch (_iamStatusError) {
            setErrorMsg(_iamStatusError.message);
            setShowErrorAlert(true);
        }
    };

    /** Set selected tenant to selectedTenant  and showWarningModal to true */
    const deleteTenantEvent = async tenant => {
        setSelectedTenant(tenant);
        setShowWarningModal(true);
    };

    /* Side effect when confirmDelete updates */
    useEffect(() => {
        if (confirmDelete === true) {
            callDeleteTenant();
        }
    }, [confirmDelete]);

    /** Column configuration for tenants table */
    const columns = [
        {
            Header: 'ID',
            accessor: 'tenantId',
            minWidth: 70,
            resizable: true,
            sortable: true,
        },
        {
            Header: 'Tenant name',
            accessor: 'name',
            minWidth: 200,
            resizable: true,
            sortable: true,
        },
        {
            Header: 'Users',
            accessor: 'users',
            minWidth: 280,
            resizable: true,
            sortable: true,
            Cell: ({ original }) => {
                let names = '';
                if (original.users && original.users.length > 0) {
                    original.users.forEach(element => {
                        names += `${element.fullName}, `;
                    });
                }
                names = names.trim();
                names = names.slice(0, names.length - 1);
                return names;
            },
        },
        {
            Header: 'IAM status',
            accessor: 'iamStatus',
            minWidth: 160,
            resizable: true,
            sortable: true,
            Cell: ({ original }) => <span style={{ paddingLeft: '0.5rem' }}>{original.iamStatus}</span>,
        },
        {
            Header: 'Actions',
            accessor: 'action',
            minWidth: 150,
            resizable: true,
            sortable: true,
            Cell: ({ original }) => (
                <div className="menu-icon">
                    <Tooltip
                        content={
                            (
                                <List>
                                    <ListItemText onClick={() => {
                                        history.push(
                                            {
                                                pathname: '/edit-tenant',
                                                state: {
                                                    tenantId: original.tenantId,
                                                },
                                            },
                                        );
                                    }}
                                    >
                                        Edit
                                    </ListItemText>
                                    <ListItemText onClick={() => deleteTenantEvent(original)}>
                                        Delete
                                    </ListItemText>
                                </List>
                            )
                        }
                        trigger="click"
                        refClassName="a-account-info"
                        className="a-account-options"
                        clickToClose
                        tooltipTheme="light"
                        style={{ zIndex: 100, width: '150px' }}
                    >
                        <span className="appkiticon icon-vertical-more-fill a-font-16 a-text-orange" aria-hidden="true" style={{ cursor: 'pointer' }} />
                    </Tooltip>
                </div>
            ),
        },
    ];

    return (
        <Tenants
            history={history}
            breadcrumbItems={breadcrumbItems}
            columns={columns}
            tableData={tableData}
            showWarningModal={showWarningModal}
            setShowWarningModal={setShowWarningModal}
            setConfirmDelete={setConfirmDelete}
            tenantLoading={tenantLoading}
            deleteTenantLoading={deleteTenantLoading}
            isChangeTenantStatusLoading={isChangeTenantStatusLoading}
            active={active}
            setActive={setActive}
            inactive={inactive}
            setInactive={setInactive}
            filterString={filterString}
            setFilterString={setFilterString}
            errorMsg={errorMsg}
            showErrorAlert={showErrorAlert}
            setShowErrorAlert={setShowErrorAlert}
            successMsg={successMsg}
            showSuccessAlert={showSuccessAlert}
            setShowSuccessAlert={setShowSuccessAlert}
        />
    );
};

export default TenantContainer;
